import { useState, useEffect, createContext, useContext } from 'react';
import { apiGet } from '../api/apiClient';
import tenantStorage from '../utils/tenantStorage';
import { setAccessMaps } from '../utils/permissions';

const SessionContext = createContext({
  displayName: 'RentTick',
  loading: true,
  permissionsVersion: 0,
  menus: [],
});

function readStoredMenus() {
  try {
    const raw = tenantStorage.getItem('menus');
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function storeMenus(menus) {
  tenantStorage.setItem('menus', JSON.stringify(menus || []));
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('renttick-auth-updated'));
  }
}

export function SessionProvider({ children }) {
  const [sessionInfo, setSessionInfo] = useState({
    displayName: 'RentTick',
    loading: true,
    permissionsVersion: 0,
    menus: readStoredMenus(),
  });

  useEffect(() => {
    const onAuthUpdated = () => {
      setSessionInfo((prev) => ({
        ...prev,
        menus: readStoredMenus(),
        permissionsVersion: (prev.permissionsVersion || 0) + 1,
      }));
    };
    window.addEventListener('renttick-auth-updated', onAuthUpdated);
    return () => window.removeEventListener('renttick-auth-updated', onAuthUpdated);
  }, []);

  useEffect(() => {
    apiGet('/tenant/info', { timeout: 10000 })
      .then(res => {
        const d = res.data;
        setSessionInfo(prev => ({
          ...prev,
          displayName: d.display_name || 'RentTick',
          appHostUrl: d.app_host_url || '',
          clientName: d.client_name || d.display_name || 'RentTick',
          addressLine1: d.address_line1 || null,
          addressLine2: d.address_line2 || null,
          city: d.city || null,
          state: d.state || null,
          postalCode: d.postal_code || null,
          country: d.country || null,
          mainPhone: d.main_phone || null,
          generalEmail: d.general_email || null,
          loading: false,
        }));
        if (d.display_name) {
          document.title = `${d.display_name} | RentTick`;
        }
      })
      .catch(() => {
        setSessionInfo(prev => ({ ...prev, loading: false }));
      });

    const token = tenantStorage.getItem('accessToken');
    if (token) {
      apiGet('/roles/me/permissions', { timeout: 10000 })
        .then((res) => {
          setAccessMaps({
            menuAccess: res.data?.menu_access || {},
            permissions: res.data?.permissions || {},
          });
          if (res.data?.user_role) {
            tenantStorage.setItem('userRole', res.data.user_role);
          }
          const menus = res.data?.menus || [];
          storeMenus(menus);
          setSessionInfo(prev => ({
            ...prev,
            menus,
            permissionsVersion: (prev.permissionsVersion || 0) + 1,
          }));
        })
        .catch(() => {});
    }
  }, []);

  return (
    <SessionContext.Provider value={sessionInfo}>
      {children}
    </SessionContext.Provider>
  );
}

export function useSessionInfo() {
  return useContext(SessionContext);
}

export default SessionContext;
